import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Grid,
    Alert,
    Typography,
    Switch,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    ListItemIcon,
} from "@mui/material";
import {
    DarkMode,
    LightMode,
    Notifications,
    NotificationsOff,
    Lock,
    Phone,
    Email,
    Badge,
    Schedule,
    Download,
    Block,
    DeleteForever,
    ChevronRight,
    VerifiedUser,
    RemoveModerator,
} from "@mui/icons-material";
import dayjs from "dayjs";
import {
    BasePage,
    LinkButton,
    Loader,
} from "components";
import { useAuthentication, useSnack, useDialog } from "hooks";
import { userService, authenticationService } from "services";
import { useTheme } from "hooks";
import { FORGOT_PASSWORD_ERROR_MESSAGES, getErrorMessage } from "constants";

export function SettingsPage() {
    const { 
        user, 
        loading, 
        sendEmailVerification,
        disableAccount,
        deleteAccount,
        isButtonLoading
    } = useAuthentication();
    const uid = useMemo(() => user?.uid || null, [user]);
    const { setThemeMode } = useTheme();
    const { openSnack } = useSnack();
    const { confirm } = useDialog();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [fetchError, setFetchError] = useState("");
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({
        theme: "light",
        emailNotifications: true,
    });

    const [savingTheme, setSavingTheme] = useState(false);
    const [savingEmailNotifs, setSavingEmailNotifs] = useState(false);
    const [exporting, setExporting] = useState(false);

    const mountedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!uid) {
                setIsLoading(false);
                return;
            }

            setFetchError("");
            setIsLoading(true);

            try {
                const doc = await userService.getUserDoc(uid);
                if (!cancelled) {
                    setProfile(doc);
                    setForm({
                        theme: doc?.settings?.theme ?? "light",
                        emailNotifications: !!doc?.settings?.notifications?.email,
                    });
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) setFetchError("Could not load settings.");
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [uid]);

    const createdAtText = useMemo(() => {
        if (!profile?.createdAt) return "";
        try {
            const ts = profile.createdAt;
            const d = typeof ts?.toDate === "function" ? ts.toDate() : ts;
            return dayjs(d).format("DD/MM/YYYY HH:mm");
        } catch {
            return String(profile.createdAt);
        }
    }, [profile]);

    // ---------- Preferences ----------
    const toggleTheme = async (e) => {
        e.stopPropagation();
        if (savingTheme) return;

        const newTheme = form.theme === "dark" ? "light" : "dark";
        const prev = form.theme;

        setForm((f) => ({ ...f, theme: newTheme }));
        setThemeMode(newTheme);
        setSavingTheme(true);

        try {
            // SEC-X2: route through PATCH /user/theme instead of direct Firestore write.
            const res = await authenticationService.makeAuthenticatedRequest(
                `${process.env.REACT_APP_API_URL}/user/theme`,
                { method: "PATCH", body: JSON.stringify({ theme: newTheme }) },
            );
            if (!res.ok) throw new Error("Failed to save theme");
            setProfile((p) => ({
                ...p,
                settings: { ...(p?.settings ?? {}), theme: newTheme },
            }));
            openSnack("Theme saved", "success");
        } catch (err) {
            console.error(err);
            setForm((f) => ({ ...f, theme: prev }));
            setThemeMode(prev);
            openSnack(err?.message || "Failed to save theme.", "error");
        } finally {
            setSavingTheme(false);
        }
    };

    const toggleEmailNotifs = async (e) => {
        e.stopPropagation();
        if (savingEmailNotifs) return;

        const next = !form.emailNotifications;
        const prev = form.emailNotifications;

        setForm((f) => ({ ...f, emailNotifications: next }));
        setSavingEmailNotifs(true);

        try {
            // SEC-X2: route through PATCH /user/notifications instead of direct Firestore write.
            const res = await authenticationService.makeAuthenticatedRequest(
                `${process.env.REACT_APP_API_URL}/user/notifications`,
                { method: "PATCH", body: JSON.stringify({ emailNotifications: next }) },
            );
            if (!res.ok) throw new Error("Failed to save notification preference");
            setProfile((p) => ({
                ...p,
                settings: {
                    ...(p?.settings ?? {}),
                    notifications: {
                        ...(p?.settings?.notifications ?? {}),
                        email: next,
                    },
                },
            }));
            openSnack("Notification preference saved", "success");
        } catch (err) {
            console.error(err);
            setForm((f) => ({ ...f, emailNotifications: prev }));
            openSnack(err?.message || "Failed to save notification preference.", "error");
        } finally {
            setSavingEmailNotifs(false);
        }
    };

    // ---------- Account actions ----------
    const handleExportData = async () => {
        setExporting(true);
        try {
            const { blob, filename } = await userService.exportUserData();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
            openSnack("Export started. Check your downloads.", "success");
        } catch (e) {
            console.error(e);
            openSnack(e?.message || "Failed to export data.", "error");
        } finally {
            setExporting(false);
        }
    };

    const handleDisableAccount = async () => {
        try {
            await confirm({
                title: "Disable your account?",
                description: "Disabling signs you out and prevents future sign-ins until re-enabled by support or via your request. Your data remains stored. Continue?",
                confirmLabel: "Disable",
                cancelLabel: "Cancel"
            });

            await disableAccount();
            openSnack("Account disabled. Signing out…", "success");
            window.location.href = "/";
        } catch (e) {
            if (e?.message === 'User cancelled') return;

            console.error(e);
            openSnack(e?.message || "Failed to disable account.", "error");
        }
    };

    const handleDeleteAccount = async () => {
        try {
            await confirm({
                title: "Delete your account?",
                description: "This action is permanent. Your profile and data may be removed. You may need to re-authenticate to complete deletion. Continue?",
                confirmLabel: "Delete",
                cancelLabel: "Cancel"
            });

            await deleteAccount();
            openSnack("Account deleted.", "success");
            window.location.href = "/";
        } catch (e) {
            if (e?.message === 'User cancelled') return;

            console.error(e);
            // Use error message constants for better UX
            const errorMessage = getErrorMessage(e, FORGOT_PASSWORD_ERROR_MESSAGES);
            openSnack(errorMessage, "error");
        }
    };

    // ---------- Dialog handlers ----------
    const handleSendEmailVerification = async () => {
        try {
            await sendEmailVerification();
            openSnack("Verification email sent", "success");
        } catch (e) {
            console.error(e);
            openSnack(e?.message || "Failed to send verification email.", "error");
        }
    };

    // Show loader if we're processing auth or still loading settings
    if (loading || isLoading) {
        return <Loader loadingMessage="Loading settings…" />;
    }

    return (
        <BasePage title="Settings">
            <Grid container spacing={3}>
                {fetchError && (
                    <Grid size={12}>
                        <Alert severity="error">{fetchError}</Alert>
                    </Grid>
                )}

                {profile && (
                    <>
                        {/* Preferences Section */}
                        <Grid size={12}>
                            <Typography variant="h6" gutterBottom>
                                Preferences
                            </Typography>
                            <List disablePadding sx={{ '& .MuiListItem-root, & .MuiListItemButton-root': { px: 0 } }}>
                                {/* Dark Mode Toggle */}
                                <ListItem
                                    secondaryAction={
                                        <Switch
                                            edge="end"
                                            checked={form.theme === "dark"}
                                            disabled={savingTheme}
                                            onClick={toggleTheme}
                                        />
                                    }
                                    sx={{ borderRadius: 1 }}
                                >
                                    <ListItemButton
                                        onClick={toggleTheme}
                                        disabled={savingTheme}
                                    >
                                        <ListItemIcon>
                                            {form.theme === "dark" ? <DarkMode /> : <LightMode />}
                                        </ListItemIcon>
                                        <ListItemText primary="Dark mode" />
                                    </ListItemButton>
                                </ListItem>

                                {/* Email Notifications Toggle */}
                                <ListItem
                                    secondaryAction={
                                        <Switch
                                            edge="end"
                                            checked={!!form.emailNotifications}
                                            disabled={savingEmailNotifs}
                                            onClick={toggleEmailNotifs}
                                        />
                                    }
                                    sx={{ borderRadius: 1 }}
                                >
                                    <ListItemButton
                                        onClick={toggleEmailNotifs}
                                        disabled={savingEmailNotifs}
                                    >
                                        <ListItemIcon>
                                            {form.emailNotifications ? <Notifications /> : <NotificationsOff />}
                                        </ListItemIcon>
                                        <ListItemText primary="Email notifications" />
                                    </ListItemButton>
                                </ListItem>
                            </List>
                        </Grid>

                        {/* Security Section */}
                        <Grid size={12}>
                            <Typography variant="h6" gutterBottom>
                                Security
                            </Typography>
                            <List disablePadding sx={{ '& .MuiListItem-root, & .MuiListItemButton-root': { px: 0 } }}>
                                <ListItemButton
                                    onClick={user?.emailVerified ? undefined : handleSendEmailVerification}
                                    disabled={user?.emailVerified || isButtonLoading}
                                    sx={{ borderRadius: 1 }}
                                >
                                    <ListItemIcon>
                                        <Email />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={user?.emailVerified ? "Email verified" : (isButtonLoading ? "Sending verification..." : "Verify email")}
                                        secondary={user?.email}
                                    />
                                    {user?.emailVerified ? <VerifiedUser /> : <RemoveModerator />}
                                </ListItemButton>

                                <ListItemButton
                                    onClick={() => navigate("/settings/reset-password")}
                                    sx={{ borderRadius: 1 }}
                                >
                                    <ListItemIcon>
                                        <Lock />
                                    </ListItemIcon>
                                    <ListItemText primary="Change password" />
                                    <ChevronRight />
                                </ListItemButton>

                                <ListItemButton
                                    onClick={() => navigate("/settings/phone-number")}
                                    sx={{ borderRadius: 1 }}
                                >
                                    <ListItemIcon>
                                        <Phone />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={user?.phoneNumber ? "Update phone number" : "Add phone number"}
                                        secondary={user?.phoneNumber || undefined}
                                    />
                                    <ChevronRight />
                                </ListItemButton>
                            </List>
                        </Grid>

                        {/* Meta Section */}
                        <Grid size={12}>
                            <Typography variant="h6" gutterBottom>
                                Meta
                            </Typography>
                            <List disablePadding sx={{ '& .MuiListItem-root, & .MuiListItemButton-root': { px: 0 } }}>
                                <ListItem sx={{ borderRadius: 1 }}>
                                    <ListItemIcon>
                                        <Badge />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="User ID"
                                        secondary={uid || "—"}
                                        sx={{
                                            '& .MuiListItemText-secondary': {
                                                fontFamily: 'monospace',
                                                fontSize: '0.875rem',
                                                wordBreak: 'break-all',
                                            }
                                        }}
                                    />
                                </ListItem>

                                <ListItem sx={{ borderRadius: 1 }}>
                                    <ListItemIcon>
                                        <Schedule />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Created at"
                                        secondary={createdAtText || "—"}
                                    />
                                </ListItem>
                            </List>
                        </Grid>

                        {/* Account Section */}
                        <Grid size={12}>
                            <Typography variant="h6" gutterBottom>
                                Account
                            </Typography>
                            <List disablePadding sx={{ '& .MuiListItem-root, & .MuiListItemButton-root': { px: 0 } }}>
                                <ListItemButton
                                    onClick={handleExportData}
                                    disabled={exporting}
                                    sx={{ borderRadius: 1 }}
                                >
                                    <ListItemIcon>
                                        <Download />
                                    </ListItemIcon>
                                    <ListItemText primary="Export my data" />
                                </ListItemButton>

                                <ListItemButton
                                    onClick={handleDisableAccount}
                                    disabled={isButtonLoading}
                                    sx={{ borderRadius: 1 }}
                                >
                                    <ListItemIcon>
                                        <Block />
                                    </ListItemIcon>
                                    <ListItemText primary="Disable account" />
                                </ListItemButton>

                                <ListItemButton
                                    onClick={handleDeleteAccount}
                                    disabled={isButtonLoading}
                                >
                                    <ListItemIcon>
                                        <DeleteForever sx={{ color: 'error.main' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Delete account" />
                                </ListItemButton>
                            </List>
                        </Grid>

                        {/* Navigation */}
                        <Grid size={12} className="form-actions">
                            <LinkButton to="/profile">Back to profile</LinkButton>
                        </Grid>
                    </>
                )}
            </Grid>
        </BasePage>
    );
}